import DateLabel from '@monorepo/common/component/DateLabel';
import { IconRegularEdit, IconSolidPlus } from '@monorepo/common/component/Icons';
import MoneyLabel from '@monorepo/common/component/MoneyLabel';
import NoData from '@monorepo/common/component/NoData';
import PeriodLabel from '@monorepo/common/component/PeriodLabel';
import React, { ReactNode } from 'react';
import { NavLink as RouterNavLink, Route, useRouteMatch } from 'react-router-dom';
import { Card, CardBody, Col, Nav, NavItem, NavLink, Row } from 'reactstrap';
import PeriodAppConfigDetailEditModal from './PeriodAppConfigDetailEditModal';

interface ConfigRowProps {
  label: string;
  value?: ReactNode;
  children?: ReactNode;
}

function ConfigRow({ label, value, children }: ConfigRowProps) {
  return (
    <Row>
      <Col xs={6} className="text-muted">
        {label}
      </Col>
      <Col xs={6} className="text-right">
        {children || value}
      </Col>
    </Row>
  );
}

function ConfigDoubleRow({ label, value, children }: ConfigRowProps) {
  return (
    <Row>
      <Col sm={12} className="text-muted">
        {label}
      </Col>
      <Col sm={12}>{children || value}</Col>
    </Row>
  );
}

interface PeriodAppConfigListProps {
  periodAppConfigs: {
    id: string;
    disciplinePaymentScheduleRecurringDate: string;
    disciplinePaymentScheduleOtherDate: string;
    disciplinePaymentScheduleSignUpDate: string;
    disciplinePaymentScheduleEndDate: string;
    stampPriceChild: number;
    stampPriceAdult: number;
    stampPriceSenior: number;
    stampPaymentEndDate: string;
    period: {
      id: string;
      year: number;
      number: number;
    };
    tokenPaymentEndDate: string;
  }[];
}

export default function PeriodAppConfigList({ periodAppConfigs }: PeriodAppConfigListProps) {
  const { url, path } = useRouteMatch();

  return (
    <Route path={`${path}/:periodId?`}>
      {({ match }) => {
        const periodId = match?.params.periodId;

        let selectedPac = periodAppConfigs.find(pac => pac.period.id === periodId);
        if (!selectedPac && periodAppConfigs.length) {
          selectedPac = periodAppConfigs[0];
        }

        return (
          <>
            <Nav tabs>
              {periodAppConfigs.map(pac => (
                <NavItem key={pac.period.id}>
                  <NavLink tag={RouterNavLink} active={pac === selectedPac} to={`${url}/${pac.period.id}`}>
                    <PeriodLabel period={pac.period} />
                  </NavLink>
                </NavItem>
              ))}
              <PeriodAppConfigDetailEditModal title="Nové nastavení aplikace">
                {(handleModalOpen, title) => (
                  <NavItem key={-1}>
                    <NavLink active={false} onClick={handleModalOpen}>
                      <span>
                        <IconSolidPlus title={title} className="action-icon text-primary" />
                      </span>
                    </NavLink>
                  </NavItem>
                )}
              </PeriodAppConfigDetailEditModal>
            </Nav>
            <Card>
              <CardBody>
                {selectedPac ? (
                  <>
                    <PeriodAppConfigDetailEditModal
                      key={selectedPac.id}
                      title="Změna nastavení aplikace"
                      periodAppConfig={selectedPac}
                    >
                      {(handleModalOpen, title) => (
                        <h4>
                          <IconRegularEdit
                            onClick={handleModalOpen}
                            title={title}
                            className="action-icon text-primary"
                          />
                          Nastavení aplikace
                        </h4>
                      )}
                    </PeriodAppConfigDetailEditModal>

                    {/* PeriodAppConfig info */}
                    {/*Discipline config offer waves*/}
                    <Row>
                      <Col sm={12}>
                        <h5>Oddíly</h5>
                      </Col>
                    </Row>

                    <ConfigDoubleRow label="Datum spuštění 1. vlny (členové z předchozího období)">
                      <DateLabel dateString={selectedPac.disciplinePaymentScheduleRecurringDate} />
                    </ConfigDoubleRow>

                    <ConfigDoubleRow label="Datum spuštění 2. vlny (ostatní členové)">
                      <DateLabel dateString={selectedPac.disciplinePaymentScheduleOtherDate} />
                    </ConfigDoubleRow>

                    <ConfigDoubleRow label="Datum spuštění 3. vlny (online registrace)">
                      <DateLabel dateString={selectedPac.disciplinePaymentScheduleSignUpDate} />
                    </ConfigDoubleRow>

                    <ConfigDoubleRow label="Datum ukončení online plateb (ke konci tohoto dne)">
                      <DateLabel dateString={selectedPac.disciplinePaymentScheduleEndDate} />
                    </ConfigDoubleRow>

                    {/*Stamp prices*/}
                    <Row className="mt-3">
                      <Col sm={12}>
                        <h5>Ceny známek</h5>
                      </Col>
                    </Row>

                    <ConfigRow label="Děti (do 18 let)">
                      <MoneyLabel amount={selectedPac.stampPriceChild} />
                    </ConfigRow>

                    <ConfigRow label="Dospělí (18 - 65 let)">
                      <MoneyLabel amount={selectedPac.stampPriceAdult} />
                    </ConfigRow>

                    <ConfigRow label="Senioři (od 65 let)">
                      <MoneyLabel amount={selectedPac.stampPriceSenior} />
                    </ConfigRow>

                    <ConfigDoubleRow label="Datum ukončení online plateb (ke konci tohoto dne)">
                      <DateLabel dateString={selectedPac.stampPaymentEndDate} />
                    </ConfigDoubleRow>

                    {/* Tokens */}
                    <Row className="mt-3">
                      <Col sm={12}>
                        <h5>Mimořádné známky</h5>
                      </Col>
                    </Row>

                    <ConfigDoubleRow label="Datum ukončení online plateb (ke konci tohoto dne)">
                      <DateLabel dateString={selectedPac.tokenPaymentEndDate} />
                    </ConfigDoubleRow>
                  </>
                ) : (
                  <>
                    <NoData text="Vyberte období" />
                  </>
                )}
              </CardBody>
            </Card>
          </>
        );
      }}
    </Route>
  );
}
